import React, { Component, Fragment } from "react";
import moment from "moment";
import styled from "styled-components";
import { Button, Icon, MenuItem } from "@material-ui/core";
import { ArrowRight } from "@material-ui/icons";

import Api from "js/core/api";
import { _ } from "js/vendor";
import { UndoType } from "common/constants";
import getUserProfile from "js/core/services/userProfiles";
import { FlexBox } from "js/react/components/LayoutGrid";
import Avatar from "js/react/components/Avatar";
import { FlexSpacer, Gap, Gap10, Gap30, Gap5 } from "js/react/components/Gap";
import { app } from "js/namespaces";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";
import { getCanvasBundle } from "js/canvas";
import { PopupMenu } from "js/react/components/PopupMenu";
import { ds } from "js/core/models/dataService";
import { appVersion } from "js/config";
import Spinner from "js/react/components/Spinner";

const RevisionsContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .loadBtn {
    width: calc(100% - 20px);
    height: 40px;
    margin: 10px;
  }
`;

const WarningMessage = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

const TimedSegmentLabel = styled.div`
  background: #f1f1f1;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0px;
  padding: 8px 12px;
`;

const RevisionGroupContainer = styled.div`
  hr {
    background: #f1f1f1;
    margin: 0px;
  }
  .expander {
    .MuiSvgIcon-root {
      font-size: 28px;
      margin: 7px -5px;
      transition: transform 300ms;
    }

    &.expanded {
      .MuiSvgIcon-root {
        transform: rotate(90deg);
      }
    }
  }
`;

const RevisionItemContainer = styled.div`
  padding: 10px 4px 10px 10px;
  width: 100%;
  background: ${props => props.isRollover ? themeColors.hoverBlue : "none"};

  .avatar {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }

  label {
    letter-spacing: 0px;
  }

  .time {
    font-weight: ${props => props.isRollover ? 600 : 500};
    font-size: 15px;
    color: black;
  }

  .hint {
    margin-top: 8px;
    background: #d7e6ec;
    text-transform: uppercase;
    color: #333;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    margin-right: 10px;
  }

  .MuiIcon-root {
    color: ${themeColors.ui_blue};
  }
`;

export class RevisionPane extends Component {
    constructor(props) {
        super(props);

        this.state = {
            page: 1,
            isEnd: false,
            revisions: [],
            isLoading: true
        };

        this._refreshSlideDebounced = _.debounce(this._refreshSlide, 200);
    }

    componentDidMount() {
        this.props.currentSlide.detachAdapter();
        this.fetchRevisions();

        this.refreshSlidePromiseChain = Promise.resolve();
    }

    componentWillUnmount() {
        this.props.currentSlide.reattachAdapter();
    }

    async fetchRevisions() {
        const { currentSlide } = this.props;
        const { page } = this.state;

        let loadedRevisions = [];

        const slideRevisions = await Api.slideRevisions.get({ id: currentSlide.id, page: page });
        if (slideRevisions.length > 1) {
            for (const slideRevision of slideRevisions) {
                const index = slideRevisions.indexOf(slideRevision);
                const { revisionTimestamp, revisionCreatedBy } = slideRevision;

                let time = moment(revisionTimestamp).format("LTS");

                if (slideRevision.revisionIsInitialState) {
                    this.setState({ isEnd: true });
                }

                const currentTemplate = slideRevision.template_id;
                const nextRevision = slideRevisions[index + 1];
                const nextTemplate = nextRevision?.template_id;
                const convertedTo = nextTemplate && nextTemplate !== currentTemplate ? currentTemplate : null;

                const state = {
                    ...currentSlide.attributes,
                    ..._.omit(slideRevision, ["slideId", "revisionTimestamp", "revisionCreatedBy", "revisionIsInitialState", "createdAt", "modifiedAt"]),
                    id: currentSlide.id
                };

                let revisionItemState = {
                    userId: revisionCreatedBy,
                    timestamp: revisionTimestamp,
                    state,
                    time,
                    isLastRevision: index === 0 && page == 1
                };

                if (slideRevision.revisionIsInitialState) {
                    revisionItemState.hint = "Initial Creation";
                } else if (convertedTo) {
                    const { slideTemplates: { slideTemplates } } = await getCanvasBundle(nextRevision.version ?? appVersion);
                    revisionItemState.hint = `Converted to ${slideTemplates[convertedTo].title}`;
                } else if (nextRevision && slideRevision.version !== nextRevision.version && slideRevision.version >= 10) {
                    revisionItemState.hint = `Updated Version to ${slideRevision.version}`;
                }

                loadedRevisions.push(revisionItemState);
            }

            this.setState({
                isLoading: false,
                revisions: this.state.revisions.concat(loadedRevisions)
            });
        } else {
            this.setState({
                isEnd: true,
                isLoading: false,
                revisions: []
            });
        }
    }

    loadNextPage = () => {
        this.setState({ page: this.state.page + 1 }, () => {
            this.fetchRevisions();
        });
    }

    handlePreviewRevision = revision => {
        const { update } = this._setSlideState(revision.state);
        this._refreshSlideDebounced(update);
    }

    _setSlideState(state) {
        return this.props.currentSlide.update(state, { removeMissingKeys: true, silent: true, save: false });
    }

    async _refreshSlide(slideUpdates) {
        const editor = app.mainView.editorView;
        const canvas = editor.currentCanvas;

        if (!canvas) {
            return;
        }

        this.refreshSlidePromiseChain = this.refreshSlidePromiseChain.then(() => editor.reloadCurrentSlide());
        return this.refreshSlidePromiseChain;
    }

    handleSaveRevision = async revision => {
        const { onClose, currentSlide } = this.props;

        this.setState({ isSaving: true });

        const isConfirmed = await ShowConfirmationDialog({
            title: `Are you sure you want to restore this slide to the version created on ${moment(revision.timestamp).format("ddd, MMM Do YYYY")} at ${revision.time}?`
        });

        if (isConfirmed) {
            this._setSlideState(revision.state);
            const { hasUpdates } = await currentSlide.saveChanges();

            if (hasUpdates && app.undoManager) {
                app.undoManager.set(
                    UndoType.SLIDE_DATA, currentSlide.id,
                    { attributes: currentSlide.originalState }, { attributes: revision.state }
                );
            }

            onClose();
        } else {
            this.handlePreviewRevision(this.state.revisions[0]);
        }
    }

    handleCopyRevision = async revision => {
        const { onClose, currentSlide } = this.props;

        const insertIndex = ds.selection.presentation.getSlideIndex(currentSlide.id) + 1;
        const [newSlide] = await ds.selection.presentation.duplicateSlide(currentSlide, { insertIndex });

        this.handlePreviewRevision(this.state.revisions[0]);

        onClose();

        _.defer(() => {
            app.mainView.editorView.goToSlide({ model: newSlide });
        });
    }

    handleMouseLeave = () => {
        if (!this.state.isSaving) {
            this.handlePreviewRevision(this.state.revisions[0]);
        }
    }

    render() {
        const { currentSlide } = this.props;
        const { revisions, isEnd, isLoading } = this.state;

        if (isLoading) {
            return <Spinner />;
        }

        if (revisions.length == 0) {
            return <WarningMessage>There are no previous versions of this slide</WarningMessage>;
        }

        const timedRevisions = _.groupBy(revisions, revision => {
            if (moment().diff(moment(revision.timestamp), "hours") < 1) {
                return "Recent versions";
            } else {
                return moment(revision.timestamp).fromNow();
            }
        });

        const groupedRevisions = {};
        for (const group of Object.keys(timedRevisions)) {
            const revisions = timedRevisions[group];

            let parentRevision = revisions[0];
            groupedRevisions[group] = [[parentRevision]];

            for (let i = 1; i < revisions.length; i++) {
                const revision = revisions[i];
                if (revision.hint) {
                    groupedRevisions[group].push([revision]);
                    parentRevision = null;
                } else {
                    if (parentRevision && parentRevision.timestamp - revision.timestamp < 1000 * 60) {
                        _.last(groupedRevisions[group]).push(revision);
                    } else {
                        groupedRevisions[group].push([revision]);
                        parentRevision = revision;
                    }
                }
            }
        }

        const revisionComponents = [];
        let revisionsRenderedSoFar = 0;

        for (const [timeLabel, revisionTimeSegment] of Object.entries(groupedRevisions)) {
            const revisionGroupComponents = [];

            for (const [i, revisionGroup] of Object.entries(revisionTimeSegment)) {
                revisionGroupComponents.push(
                    <RevisionGroup key={i}
                        expanded={revisionsRenderedSoFar < 5}
                        currentSlide={currentSlide}
                        revisionGroup={revisionGroup}
                        onPreviewRevision={this.handlePreviewRevision}
                        onSaveRevision={this.handleSaveRevision}
                        onCopyRevision={this.handleCopyRevision}
                    />
                );
                revisionsRenderedSoFar += revisionGroup.length;
            }

            revisionComponents.push(
                <Fragment key={timeLabel}>
                    <TimedSegmentLabel key={timeLabel}>{timeLabel}</TimedSegmentLabel>
                    {revisionGroupComponents}
                </Fragment>
            );
        }

        return (
            <RevisionsContainer onMouseLeave={this.handleMouseLeave}>
                {revisionComponents}
                <FlexSpacer />
                {!isEnd &&
                    <Button className="loadBtn" onClick={this.loadNextPage}>Load more</Button>
                }
            </RevisionsContainer>
        );
    }
}

class RevisionGroup extends Component {
    constructor(props) {
        super(props);

        this.state = {
            expanded: props.expanded ?? false
        };
    }

    handleToggleExpand = () => {
        this.setState({
            expanded: !this.state.expanded
        });
    }

    render() {
        const { revisionGroup, currentSlide, onPreviewRevision, onSaveRevision, onCopyRevision } = this.props;
        const { expanded } = this.state;

        return (
            <RevisionGroupContainer>
                <FlexBox left middle>
                    {revisionGroup.length > 1 &&
                        <div className={`expander ${expanded ? "expanded" : "collapsed"}`}
                            onClick={this.handleToggleExpand}>
                            <ArrowRight />
                        </div>
                    }
                    {revisionGroup.length === 1 &&
                        <Gap size={18} />
                    }
                    <RevisionItem key={revisionGroup[0].id}
                        currentSlide={currentSlide}
                        revision={revisionGroup[0]}
                        onPreviewRevision={onPreviewRevision}
                        onSaveRevision={onSaveRevision}
                        onCopyRevision={onCopyRevision}
                    />
                </FlexBox>
                <hr />
                {expanded &&
                    _.drop(revisionGroup, 1).map(revision => (
                        <>
                            <FlexBox left >
                                <Gap30 />
                                <RevisionItem key={revision.id}
                                    currentSlide={currentSlide}
                                    revision={revision}
                                    onPreviewRevision={onPreviewRevision}
                                    onSaveRevision={onSaveRevision}
                                    onCopyRevision={onCopyRevision}
                                />
                            </FlexBox>
                            <hr />
                        </>
                    ))
                }
            </RevisionGroupContainer>
        );
    }
}

class RevisionItem extends Component {
    state = {
        photoUrl: null,
        userName: "",
        isShowingPopup: false,
        isRollover: false
    }

    componentDidMount() {
        let { userId } = this.props.revision;

        getUserProfile(userId).then(user => {
            this.setState({
                photoUrl: user.photoURL,
                userName: user.displayName || user.email
            });
        });
    }

    handleMouseEnter = () => {
        this.setState({ isRollover: true });
        this.props.onPreviewRevision(this.props.revision);
    }

    handleMouseLeave = () => {
        this.setState({ isRollover: false });
    }

    render() {
        const { revision, onSaveRevision, onCopyRevision } = this.props;
        const { photoUrl, userName, isRollover } = this.state;

        return (
            <RevisionItemContainer
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onClick={() => onSaveRevision(revision)}
                isRollover={isRollover}
            >
                <FlexBox left fill>
                    <FlexBox left top vertical>
                        <label className="time">{moment(revision.timestamp).format("MMM D")}, {revision.time}</label>
                        <Gap5 />
                        <FlexBox left middle>
                            <Avatar url={photoUrl} />
                            <label className="username">{userName}</label>
                        </FlexBox>
                    </FlexBox>
                    <FlexSpacer />
                    {isRollover &&
                        <PopupMenu icon="more_vert"
                            childrenAreMenuItems
                            style={{ padding: 0 }}
                            onShow={() => this.setState({ isShowingPopup: true })}
                            onClose={() => this.setState({ isShowingPopup: false })}
                        >
                            <MenuItem onClick={() => onSaveRevision(revision)} divider><Icon>history</Icon><Gap10 />Restore
                                this Version</MenuItem>
                            <MenuItem onClick={() => onCopyRevision(revision)}><Icon>content_copy</Icon><Gap10 />Copy
                                this Version to a New Slide</MenuItem>
                        </PopupMenu>
                    }
                </FlexBox>

                {revision.hint && <div className="hint">{revision.hint}</div>}
            </RevisionItemContainer>
        );
    }
}

